import { DateTimePeriodParams } from '@brenger/api-client';
import { getHours } from 'date-fns';
import React from 'react';
import { Translate } from 'react-localize-redux-dep-updated';
import { useDispatch } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { store } from '../../..';
import {
  FlashMessage,
  Heading,
  IconSmile,
  InputTiles,
  SheetLayoutContent,
  SheetLayoutContentWrapper,
  SheetLayoutHeader,
} from '../../../brenger-shared-ui';
import { useTypedSelector } from '../../../hooks';
import { r } from '../../../routes';
import { RootState } from '../../../types';
import { printDatesAsString } from '../../../utils/datetime';
import {
  GENERAL_DEFAULT_TIME_END,
  GENERAL_DEFAULT_TIME_END_STORE,
  GENERAL_DEFAULT_TIME_START,
  GENERAL_DEFAULT_TIME_START_STORE,
  TIME_SLOT_LENGTH,
} from '../../../utils/global';
import FooterLayout from '../containers/FooterLayout';
import {
  actions,
  getFlowTitleByStepAndSituation,
  getPickupSituation,
  getStopAvailableDate,
  getStopSelectedTime,
  getStopSituation,
  isSameDayDelivery,
} from '../ducks';
import { useTimeOptions } from '../hooks/useTimeOptions';
import { proceed } from '../proceed';
import { TimeCustomOptionsBusinessHours } from './TimeCustomOptionsBusinessHours';
import { getAccountHasBusinessFlow } from '../../User/ducks';
import { translate } from '../../../utils/localization';

export const SubStepTime: React.FC<RouteComponentProps> = ({ match, location }) => {
  const dispatch = useDispatch();
  // get state
  const stopType = match.path === r.generalFlow.time.index() ? 'pickup' : 'delivery';
  // The date is also included in the time options, but we use the selector more convenient,
  // and more correct for the user has selected two dates.
  const selectedDate = useTypedSelector(state => getStopAvailableDate(state, stopType));

  // Get options
  const {
    options: { full, custom },
    loading,
  } = useTimeOptions(stopType);
  // situation
  const situation = useTypedSelector((state: RootState) => getStopSituation(state, stopType));
  const isPickupAndAuctionOrStore = situation === 'auction' || situation === 'store';

  // cosmetics
  const title = useTypedSelector((state: RootState) =>
    getFlowTitleByStepAndSituation(state, stopType === 'pickup' ? 'timePickup' : 'timeDelivery')
  );
  const subTitle = useTypedSelector((state: RootState) =>
    getFlowTitleByStepAndSituation(state, stopType === 'pickup' ? 'timeCustomPickup' : 'timeCustomDelivery')
  );
  const error = useTypedSelector((state: RootState) => state.generalTransport.error);

  React.useEffect(() => {
    dispatch(actions.resetChoice([`${stopType}_time`]));
    dispatch(actions.getPrice());
    dispatch(actions.setProgress(4));
  }, [stopType, location.pathname]);

  /**
   * Check if we deal with custom business hours
   */
  const hasBusinessFlow = useTypedSelector(getAccountHasBusinessFlow);
  const isBusinessHoursOnly = hasBusinessFlow && stopType === 'pickup' && situation === 'store' && full.length === 1;

  return (
    <SheetLayoutContentWrapper>
      <SheetLayoutHeader>
        <Heading size={2}>
          <Translate
            id={title}
            data={{
              date: <span className={'text-primary'}>{printDatesAsString(selectedDate)}</span>,
            }}
          />
        </Heading>
      </SheetLayoutHeader>
      <SheetLayoutContent>
        <div className={'question-wrapper'}>
          {full.length !== 0 && (
            <>
              <div className={'mb-6'}>
                <div className="mb-2 text-sm">
                  <div
                    dangerouslySetInnerHTML={{
                      // eslint-disable-next-line @typescript-eslint/naming-convention
                      __html: translate('request_flow.time.first_option.introduction_' + situation),
                    }}
                  />
                </div>
                <InputTiles
                  id={`time_${stopType}`}
                  compact={true}
                  name={`time_${stopType}`}
                  options={full}
                  onChange={time => dispatch(actions.setChoice({ [`${stopType}_time`]: time }))}
                  onClick={() => proceed()}
                />
              </div>
              {!isBusinessHoursOnly && (
                <div className="mb-1">
                  <div className="mb-2 text-sm">
                    <div
                      dangerouslySetInnerHTML={{
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        __html: translate(subTitle),
                      }}
                    />
                  </div>
                  {isPickupAndAuctionOrStore && (
                    <div>
                      <Translate
                        id={'request_flow.time.custom_time_explain'}
                        data={{
                          time_slot: TIME_SLOT_LENGTH,
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          {!isBusinessHoursOnly && isPickupAndAuctionOrStore && <TimeCustomOptionsBusinessHours showError={!!error} />}
          {loading && !isPickupAndAuctionOrStore && <IconSmile spinning={true} />}
          {!loading && !isPickupAndAuctionOrStore && (
            <InputTiles
              id={`custom_time_${stopType}`}
              name={`custom_time_${stopType}`}
              options={custom}
              compact={true}
              onChange={(time: DateTimePeriodParams) => {
                dispatch(actions.setChoice({ [`${stopType}_time`]: time }));
                if (stopType === 'delivery') sideEffectDeliveryTime(time);
              }}
              onClick={() => proceed()}
            />
          )}
          {error && <FlashMessage type={'error'} message={error} />}
        </div>
      </SheetLayoutContent>
      <FooterLayout customSubmitFunction={() => proceed()} />
    </SheetLayoutContentWrapper>
  );
};

/**
 * To not overload above component with this logic, this is handled "aside".
 * Downside is that we have to import the store manually, but that's it
 */
const sideEffectDeliveryTime = (deliveryTime: DateTimePeriodParams): void => {
  const state = store.getState() as RootState;
  const sameDayDelivery = isSameDayDelivery(state);
  const pickupTime = getStopSelectedTime(state, 'pickup');

  /**
   * DOUBLE CHECK ON SELECTED DELIVERY
   * - When it is same day delivery
   * - When pickup time is full time slot (so not a custom time)
   * - When the user has selected an early delivery, meaning start before 13.00, end before 17:00.
   * => it means pickup isn't flexible, so we update the pickup time with the delivery times
   */
  // Check same day, is delivery and if pickup time is present
  if (!sameDayDelivery || !pickupTime) return;
  // Setup pickup times
  const pickupStartHour = getHours(new Date(pickupTime.start));
  const pickupEndHour = getHours(new Date(pickupTime.end));

  // Is pickup time a full timeslot?
  // --> store aware, because the full time slot has different times
  const isStore = getPickupSituation(state) === 'store';
  const fullTimeSlotStart = isStore ? GENERAL_DEFAULT_TIME_START_STORE : GENERAL_DEFAULT_TIME_START;
  const fullTimeSlotEnd = isStore ? GENERAL_DEFAULT_TIME_END_STORE : GENERAL_DEFAULT_TIME_END;
  if (pickupStartHour !== fullTimeSlotStart || pickupEndHour !== fullTimeSlotEnd) return;

  // Check delivery time
  const deliveryStartHour = getHours(new Date(deliveryTime.start));
  const deliveryEndHour = getHours(new Date(deliveryTime.end));

  // Do we need to adjust Pickup time?
  if (deliveryStartHour < 13 && deliveryEndHour < 17) {
    store.dispatch(actions.setChoice({ pickup_time: deliveryTime }));
    store.dispatch(actions.getPrice());
  }
};
